import React from 'react';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
  formButtonList,
} from '@/src/strategies/util';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import { get } from 'lodash';
import Animation from 'src/components/Modules-revamp/Animation';
import ModuleWidth from 'src/components/Modules-revamp/ModuleWidthSelect';
import GridType from '@/src/components/Modules-revamp/GridType';
import CallToActionButtonTheme from '@/src/components/Modules-revamp/CallToActionButtonTheme';
import ItemInARow from '@/src/components/Modules-revamp/ItemInARow';
import Products from '@/src/components/Modules-revamp/Products';
import productGridRenderTemplate from 'src/strategies/render/productGrid';
import LeaveGapBetweenProducts from '@/src/components/Modules-revamp/LeaveGapBetween';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import Text from '@/src/components/Modules-revamp/Text';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import OverflowItemsDisplayOption from '@/src/components/Modules-revamp/OverflowItemsDisplayOption';
import moduleStyleRender from '../../ModuleStyle';
import { ContentModuleGroupPreloadingContext } from '@/src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import { Field } from '@/src/components/RcForm';
import { useProductGrid } from '../../hooks/useProductGrid';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
};

const productGridRenderer = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const {
    handleOnSectionReset,
    handleOnSectionCopy,
    salesChannelMode,
    disabled,
  } = extra;
  const list = productGridRenderTemplate(values, breakpoint, salesChannelMode);

  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f, disabled));
    } else {
      return field.map((f) => productGridComponent(f, breakpoint, extra));
    }
  };
  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={get(item, 'validator')}
              disabled={disabled}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const productGridComponent = (
  data: ResponsiveFieldProps,
  breakpoint: EPreviewSize,
  extra: any,
) => {
  const { animations, hoverAnimations, form, buttons, disabled } = extra || {};
  const { onSort, onDelete, onReselect } = extra;
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  switch (data.field) {
    case ResponsiveField.GridType: {
      return (
        <GridType
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.ProductInRow: {
      return (
        <ItemInARow
          moduleType={EnumModuleType.ResponsiveProductGrid}
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.CallToActionButton: {
      return (
        <CallToActionButtonTheme
          options={formButtonList(buttons)}
          disabled={data?.disabled || disabled}
        />
      );
    }
    case ResponsiveField.LeaveGap: {
      return (
        <LeaveGapBetweenProducts
          moduleType={EnumModuleType.ResponsiveProductGrid}
          options={data?.options || []}
          disabled={data?.disabled || disabled}
        />
      );
    }
    case ResponsiveField.OverflowItems: {
      return (
        <OverflowItemsDisplayOption
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled || disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.Products: {
      return (
        <Products
          draggableId={draggableId}
          disabled={data?.disabled || disabled}
          moduleType={EnumModuleType.ProductCarousel}
          breakpoint={breakpoint}
          onSort={onSort}
          onDelete={onDelete}
          onReselect={onReselect}
        />
      );
    }
    case ResponsiveField.ProductText: {
      return (
        <Text
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          disabled={data?.disabled || disabled}
        />
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }: any) => {
  const { animations, hoverAnimations, buttons } = React.useContext(
    ContentModuleGroupPreloadingContext,
  );
  const { handleOnDelete, handleOnSort, handleOnReselect } = useProductGrid();
  return (
    <Field shouldUpdate>
      {() =>
        productGridRenderer(form, breakpoint, {
          animations,
          buttons,
          hoverAnimations,
          form,
          onSort: handleOnSort,
          onDelete: handleOnDelete,
          onReselect: handleOnReselect,
          ...extra,
        })
      }
    </Field>
  );
};
